import React, { useMemo, useState } from 'react';
import { Table, Button, Tag, Tooltip, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TablePaginationConfig } from 'antd/es/table';
import { LockOutlined, ColumnHeightOutlined } from '@ant-design/icons';
import { CustomAttributesTags } from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import BatchInventoryHoldModal from '@components/shared/inventory/BatchInventoryHoldModal';
import QuickStockAdjustmentModal from '@components/shared/inventory/QuickStockAdjustmentModal';
import InventoryHoldsModal, { type HoldServiceLike } from '@components/shared/products/InventoryHoldsModal';

interface InventoryRow {
  id: string;
  inventory_type?: string;
  batch?: string | null;
  expiration_date?: string | null;
  manufacturing_date?: string | null;
  custom_attributes?: Record<string, unknown> | null;
  quantity?: number | null;
  available_quantity?: number | null;
  allocated_quantity?: number | null;
  held_quantity?: number | null;
  pending_putaway_quantity?: number | null;
  product_sku?: string;
  status?: string;
  [key: string]: unknown;
}

interface InventoryServiceLike {
  [key: string]: unknown;
}

interface ProductInventoryTableProps {
  inventories?: InventoryRow[];
  loading?: boolean;
  inventoryService: InventoryServiceLike;
  holdService?: HoldServiceLike;
  merchantId?: string;
  pagination?: false | TablePaginationConfig;
  role?: 'operator' | 'admin';
}

/**
 * ProductInventoryTable - Displays inventory list for a product
 */
const ProductInventoryTable: React.FC<ProductInventoryTableProps> = ({
  inventories = [],
  loading = false,
  inventoryService,
  holdService,
  merchantId,
  pagination,
  role = 'operator',
}) => {
  const { t } = useTranslation();

  // Map attribute name → label for the shared CustomAttributesTags component.
  // Falls back to raw key in the tag if a definition isn't present for the merchant.
  const attributeDefinitions = useInventoryAttributeDefinitions(role, merchantId);
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map(def => [def.name, def.label ?? def.name])),
    [attributeDefinitions],
  );

  const [holdModalVisible, setHoldModalVisible] = useState(false);
  const [holdSelectedIds, setHoldSelectedIds] = useState<Set<string>>(new Set());
  const [quickAdjustVisible, setQuickAdjustVisible] = useState(false);
  const [quickAdjustInventory, setQuickAdjustInventory] = useState<InventoryRow | null>(null);
  const [holdsModalVisible, setHoldsModalVisible] = useState(false);
  const [holdsModalInventory, setHoldsModalInventory] = useState<InventoryRow | null>(null);

  const statusColors: Record<string, string> = {
    in_stock: 'success',
    out_of_stock: 'default',
    blocked: 'error'
  };

  const columns: ColumnsType<InventoryRow> = useMemo(() => [
    // ── 庫存屬性 ──
    {
      title: t('models.inventory.inventory_type'),
      dataIndex: 'inventory_type',
      key: 'inventory_type',
      width: 120,
      render: (text: string | undefined) => text || '-',
    },
    {
      title: t('models.inventory.batch'),
      dataIndex: 'batch',
      key: 'batch',
      width: 120,
      render: (text: string | null | undefined) => text || '-',
    },
    {
      title: t('models.inventory.expiration_date'),
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 120,
      render: (text: string | null | undefined) => text || '-',
    },
    {
      title: t('models.inventory.manufacturing_date'),
      dataIndex: 'manufacturing_date',
      key: 'manufacturing_date',
      width: 120,
      render: (text: string | null | undefined) => text || '-',
    },
    {
      title: t('models.inventory.custom_attributes'),
      dataIndex: 'custom_attributes',
      key: 'custom_attributes',
      width: 200,
      render: (custom_attributes: Record<string, unknown> | null | undefined) => <CustomAttributesTags attributes={custom_attributes} attributeLabelByName={attributeLabelByName} />
    },
    // ── 數量 ──
    {
      title: t('models.inventory.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      align: 'right',
      render: (value: number | null | undefined) => value || 0,
    },
    {
      title: t('models.inventory.available_quantity'),
      dataIndex: 'available_quantity',
      key: 'available_quantity',
      width: 120,
      align: 'right',
      render: (value: number | null | undefined) => value || 0,
    },
    {
      title: t('models.inventory.allocated_quantity'),
      dataIndex: 'allocated_quantity',
      key: 'allocated_quantity',
      width: 100,
      align: 'right',
      render: (value: number | null | undefined, record: InventoryRow) => {
        if (!value || value === 0) return 0;
        return (
          <Link to={`/inventories/${record.id}/orders`} style={{ color: '#1890ff', fontWeight: 'bold' }}>
            {value}
          </Link>
        );
      },
    },
    {
      title: t('models.inventory.held_quantity'),
      dataIndex: 'held_quantity',
      key: 'held_quantity',
      width: 100,
      align: 'right',
      render: (value: number | null | undefined, record: InventoryRow) => {
        const qty = value || 0;
        // operator：> 0 時數字可點，開彈窗顯示此筆 inventory 的保留單明細。
        // admin / 無 holdService / = 0：維持原本的 Tag 或純數字呈現。
        if (role === 'operator' && qty > 0 && holdService) {
          return (
            <Button
              type="link"
              style={{ padding: 0, height: 'auto', color: '#1890ff', fontWeight: 'bold' }}
              onClick={(e) => {
                e.stopPropagation();
                setHoldsModalInventory(record);
                setHoldsModalVisible(true);
              }}
            >
              {qty}
            </Button>
          );
        }
        return qty > 0 ? <Tag color="warning">{qty}</Tag> : qty;
      },
    },
    {
      title: t('models.inventory.pending_putaway_quantity'),
      dataIndex: 'pending_putaway_quantity',
      key: 'pending_putaway_quantity',
      width: 110,
      align: 'right',
      render: (value: number | null | undefined) => {
        const qty = value || 0;
        return qty > 0 ? <Tag color="orange">{qty}</Tag> : qty;
      },
    },
    // ── 狀態 ──
    {
      title: t('models.inventory.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>
          {t(`enums.inventory.status.${status}`)}
        </Tag>
      ),
    },
    // ── 操作 ──
    {
      title: t('common.actions'),
      key: 'action',
      width: 160,
      fixed: 'right',
      render: (_: unknown, record: InventoryRow) => (
        <Space size={0}>
          {holdService && (record.available_quantity ?? 0) > 0 && (
            <Tooltip title={t('shared.inventory_holds.quick_hold')}>
              <Button
                type="text"
                icon={<LockOutlined style={{ color: '#faad14' }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  setHoldSelectedIds(new Set([record.id]));
                  setHoldModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          {role === 'operator' && (
            <Tooltip title={t('shared.stock_adjustment.quick_adjust_title')}>
              <Button
                type="text"
                icon={<ColumnHeightOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  setQuickAdjustInventory(record);
                  setQuickAdjustVisible(true);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ], [t, holdService, statusColors, attributeLabelByName, role]);

  return (
    <>
      <Table
        columns={columns}
        dataSource={inventories}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        scroll={{ x: 'max-content' }}
      />

      {holdService && (
        <BatchInventoryHoldModal
          visible={holdModalVisible}
          selectedIds={holdSelectedIds}
          inventoryService={inventoryService}
          holdService={holdService}
          merchantId={merchantId}
          onCancel={() => setHoldModalVisible(false)}
          onSuccess={() => window.location.reload()}
        />
      )}

      {role === 'operator' && (
        <QuickStockAdjustmentModal
          visible={quickAdjustVisible}
          inventory={quickAdjustInventory}
          inventoryService={inventoryService}
          onCancel={() => setQuickAdjustVisible(false)}
          onSuccess={() => window.location.reload()}
        />
      )}

      {role === 'operator' && holdService && (
        <InventoryHoldsModal
          open={holdsModalVisible}
          inventoryId={holdsModalInventory?.id}
          productSku={holdsModalInventory?.product_sku}
          batch={holdsModalInventory?.batch}
          holdService={holdService}
          onClose={() => setHoldsModalVisible(false)}
        />
      )}
    </>
  );
};

export default ProductInventoryTable;
